import { useState } from "react";
import { useBudget } from "../context/BudgetContext";

const DEPT_COLORS = ["var(--green)", "var(--blue)", "var(--amber)", "var(--purple)", "var(--red)", "#34d399", "#fb923c"];
const HOVER_CLASSES = ["hover-green", "hover-blue", "hover-amber", "hover-purple", "hover-red", "hover-green", "hover-amber"];

export default function Departments() {
  const { transactions, monthlyBudget, budgetLoading, departments, updateDepartments } = useBudget();
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editBudget, setEditBudget] = useState("");
  const [editHeadcount, setEditHeadcount] = useState("");

  if (budgetLoading) return <div className="page-loading" />;

  const totalAllocated = departments.reduce((s, d) => s + Number(d.budget), 0);
  const unallocated = monthlyBudget - totalAllocated;

  const addDept = () => {
    if (!newName.trim()) return;
    const colorIdx = departments.length % DEPT_COLORS.length;
    updateDepartments([...departments, { id: Date.now(), name: newName.trim(), budget: 0, headcount: 0, colorIdx }]);
    setNewName("");
  };

  const deleteDept = (id) => updateDepartments(departments.filter(d => d.id !== id));

  const saveEdit = (id) => {
    updateDepartments(departments.map(d =>
      d.id === id ? { ...d, budget: Number(editBudget) || d.budget, headcount: Number(editHeadcount) || d.headcount } : d
    ));
    setEditingId(null);
  };

  const getSpend = () =>
    transactions.length > 0 && departments.length > 0
      ? Math.round(transactions.reduce((s, tx) => s + Number(tx.amount), 0) / departments.length)
      : 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Departments</div>
          <div className="page-subtitle">$ budget allocation by team</div>
        </div>
      </div>

      {/* Allocation bar */}
      <div className="card hover-blue" style={{ marginBottom: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <div className="label" style={{ marginBottom: 0 }}>BUDGET_ALLOCATION</div>
          <span className="mono" style={{ fontSize: 10, color: "#666" }}>${totalAllocated.toLocaleString()} / ${monthlyBudget.toLocaleString()}</span>
        </div>
        <div style={{ height: 2, background: "#0d0d0d", borderRadius: 1, overflow: "hidden", display: "flex" }}>
          {departments.filter(d => d.budget > 0 && monthlyBudget > 0).map(d => (
            <div key={d.id} style={{ height: "100%", width: `${(d.budget / monthlyBudget) * 100}%`, background: DEPT_COLORS[d.colorIdx], transition: "width 0.4s" }} />
          ))}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px", marginTop: 8 }}>
          {departments.map(d => (
            <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 4, height: 4, borderRadius: "50%", background: DEPT_COLORS[d.colorIdx] }} />
              <span className="mono" style={{ fontSize: 9, color: "#777" }}>{d.name.toLowerCase()}</span>
            </div>
          ))}
          {unallocated > 0 && monthlyBudget > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#1a1a1a" }} />
              <span className="mono" style={{ fontSize: 9, color: "#555" }}>unallocated ${unallocated.toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>

      {/* Add dept */}
      <div className="card hover-green" style={{ marginBottom: 8 }}>
        <div className="label" style={{ marginBottom: 8 }}>ADD_DEPARTMENT</div>
        <div style={{ display: "flex", gap: 6 }}>
          <input placeholder="e.g. design" value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === "Enter" && addDept()} />
          <button className="btn btn-green" onClick={addDept}>+ add</button>
        </div>
      </div>

      {/* Dept cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 8 }}>
        {departments.map(dept => {
          const spend = getSpend();
          const pct = dept.budget > 0 ? Math.min((spend / dept.budget) * 100, 100) : 0;
          const fillClass = pct >= 100 ? "red" : pct >= 80 ? "amber" : "";
          const color = DEPT_COLORS[dept.colorIdx];
          const hoverClass = HOVER_CLASSES[dept.colorIdx];
          const costPerHead = dept.headcount > 0 ? Math.round(dept.budget / dept.headcount) : null;

          return (
            <div key={dept.id} className={`card ${hoverClass}`} style={{ borderTop: `2px solid ${color}22` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span className="mono" style={{ fontSize: 12, color: "#888" }}>{dept.name.toLowerCase()}</span>
                <button className="btn btn-danger" onClick={() => deleteDept(dept.id)} style={{ padding: "2px 7px", fontSize: 9 }}>del</button>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <span className="label" style={{ marginBottom: 0 }}>spend</span>
                <span className="mono" style={{ fontSize: 10, color: "#666" }}>
                  ${spend.toLocaleString()}{dept.budget > 0 ? ` / $${dept.budget.toLocaleString()}` : ""}
                </span>
              </div>
              {dept.budget > 0 && (
                <div className="progress-track" style={{ marginBottom: 8 }}>
                  <div className={`progress-fill ${fillClass}`} style={{ width: `${pct}%`, background: color }} />
                </div>
              )}

              {dept.headcount > 0 && (
                <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                  <div style={{ flex: 1, background: "#050505", borderRadius: 4, padding: "5px 8px" }}>
                    <div className="label" style={{ marginBottom: 1 }}>headcount</div>
                    <span className="mono" style={{ fontSize: 11, color: "#555" }}>{dept.headcount}</span>
                  </div>
                  {costPerHead && (
                    <div style={{ flex: 1, background: "#050505", borderRadius: 4, padding: "5px 8px" }}>
                      <div className="label" style={{ marginBottom: 1 }}>cost/head</div>
                      <span className="mono" style={{ fontSize: 11, color: "#555" }}>${costPerHead.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              )}

              {editingId === dept.id ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <input type="number" placeholder="budget ($)" value={editBudget} onChange={e => setEditBudget(e.target.value)} autoFocus />
                  <input type="number" placeholder="headcount" value={editHeadcount} onChange={e => setEditHeadcount(e.target.value)} />
                  <div style={{ display: "flex", gap: 5 }}>
                    <button className="btn btn-green" style={{ flex: 1, fontSize: 10 }} onClick={() => saveEdit(dept.id)}>save</button>
                    <button className="btn" style={{ fontSize: 10 }} onClick={() => setEditingId(null)}>✕</button>
                  </div>
                </div>
              ) : (
                <button className="btn" style={{ width: "100%", fontSize: 10 }}
                  onClick={() => { setEditingId(dept.id); setEditBudget(dept.budget || ""); setEditHeadcount(dept.headcount || ""); }}>
                  {dept.budget > 0 ? `edit — $${dept.budget.toLocaleString()}` : "set budget & headcount"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
